import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getLevel } from '../utils/levelSystem';
import './AppShell.css';

function AppShell({ children }) {
    const { currentUser, userData, logout } = useAuth();
    const location = useLocation();
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // Pages that should NOT show the app shell (landing, auth, games, world-map)
    const noShellPages = ['/', '/auth', '/game', '/world-map', '/interactive-lesson', '/lesson-complete'];
    const showShell = currentUser && !noShellPages.includes(location.pathname);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Close profile menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (showProfileMenu &&
                !e.target.closest('.profile-section') &&
                !e.target.closest('.mobile-profile-menu') &&
                !e.target.closest('.profile-menu')) {
                setShowProfileMenu(false);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [showProfileMenu]);

    const handleLogout = async () => {
        await logout();
        setShowProfileMenu(false);
    };

    // Hardcoded admin emails - matches server.js and AdminRoute.js
    const ADMIN_EMAILS = ['bspencerhooah@gmail.com'];
    const isAdmin = ADMIN_EMAILS.includes(currentUser?.email?.toLowerCase()) ||
                    ['admin', 'super_admin'].includes(userData?.role);

    const navItems = [
        { path: '/home', icon: '🏠', label: 'Home', activeIcon: '🏡' },
        { path: '/dashboard', icon: '📊', label: 'Dashboard', activeIcon: '📈' },
        { path: '/flashcards', icon: '🃏', label: 'Cards', activeIcon: '🎴' },
        { path: '/progress', icon: '🏆', label: 'Progress', activeIcon: '🥇' },
        // Admin link - only shown to admins
        ...(isAdmin ? [{ path: '/admin', icon: '⚙️', label: 'Admin', activeIcon: '🔧' }] : []),
    ];

    const getInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    if (!showShell) {
        return <>{children}</>;
    }

    return (
        <div className="app-shell">
            {/* Desktop Sidebar */}
            {!isMobile && (
                <aside className="sidebar">
                    <div className="sidebar-header">
                        <NavLink to="/home" className="sidebar-logo">
                            <span className="logo-icon">📚</span>
                            <span className="logo-text">LearnViva</span>
                        </NavLink>
                    </div>

                    <nav className="sidebar-nav">
                        {navItems.map((item) => (
                            <NavLink
                                key={item.path}
                                to={item.path}
                                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                            >
                                {({ isActive }) => (
                                    <>
                                        <span className="nav-icon">{isActive ? item.activeIcon : item.icon}</span>
                                        <span className="nav-label">{item.label}</span>
                                        {isActive && <div className="nav-indicator" />}
                                    </>
                                )}
                            </NavLink>
                        ))}
                    </nav>

                    {/* Sidebar Stats */}
                    {(() => {
                        const levelInfo = getLevel(userData?.totalXP || 0);
                        return (
                            <div className="sidebar-stats">
                                <div className="stat-item xp">
                                    <span className="stat-icon">⚡</span>
                                    <div className="stat-info">
                                        <span className="stat-value">{userData?.totalXP?.toLocaleString() || 0}</span>
                                        <span className="stat-label">XP</span>
                                    </div>
                                </div>
                                <div className="stat-item streak">
                                    <span className="stat-icon">🔥</span>
                                    <div className="stat-info">
                                        <span className="stat-value">{userData?.streak || 0}</span>
                                        <span className="stat-label">Day Streak</span>
                                    </div>
                                </div>
                                <div className="stat-item level">
                                    <span className="stat-icon">{levelInfo.icon}</span>
                                    <div className="stat-info">
                                        <span className="stat-value">{levelInfo.level}</span>
                                        <span className="stat-label">Level</span>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}

                    {/* Profile Section */}
                    <div className="profile-section">
                        <button
                            className="profile-trigger"
                            onClick={() => setShowProfileMenu(!showProfileMenu)}
                        >
                            <div className="profile-avatar">
                                {getInitials(userData?.displayName || currentUser?.email)}
                            </div>
                            <div className="profile-info">
                                <span className="profile-name">
                                    {userData?.displayName || 'Learner'}
                                </span>
                                <span className="profile-email">
                                    {currentUser?.email}
                                </span>
                            </div>
                            <span className={`profile-arrow ${showProfileMenu ? 'open' : ''}`}>▾</span>
                        </button>

                        {showProfileMenu && (
                            <div className="profile-menu">
                                <NavLink to="/profile" className="menu-item" onClick={() => setShowProfileMenu(false)}>
                                    <span className="menu-icon">👤</span>
                                    <span>Profile</span>
                                </NavLink>
                                <NavLink to="/analytics" className="menu-item" onClick={() => setShowProfileMenu(false)}>
                                    <span className="menu-icon">📈</span>
                                    <span>Analytics</span>
                                </NavLink>
                                <div className="menu-divider" />
                                <button className="menu-item logout" onClick={handleLogout}>
                                    <span className="menu-icon">🚪</span>
                                    <span>Sign Out</span>
                                </button>
                            </div>
                        )}
                    </div>
                </aside>
            )}

            {/* Main Content */}
            <main className="main-content">
                {/* Mobile Top Bar */}
                {isMobile && (() => {
                    const levelInfo = getLevel(userData?.totalXP || 0);
                    return (
                        <header className="mobile-header">
                            <NavLink to="/home" className="mobile-logo">
                                <span className="logo-icon">📚</span>
                            </NavLink>

                            <div className="mobile-stats">
                                <div className="mobile-stat">
                                    <span>⚡</span>
                                    <span>{userData?.totalXP?.toLocaleString() || 0}</span>
                                </div>
                                <div className="mobile-stat streak">
                                    <span>🔥</span>
                                    <span>{userData?.streak || 0}</span>
                                </div>
                            </div>

                            <button
                                className="mobile-profile profile-section"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowProfileMenu(!showProfileMenu);
                                }}
                            >
                                <div className="profile-avatar small">
                                    {getInitials(userData?.displayName || currentUser?.email)}
                                </div>
                            </button>

                            {showProfileMenu && (
                                <div className="mobile-profile-menu">
                                    <div className="menu-header">
                                        <div className="profile-avatar">
                                            {getInitials(userData?.displayName || currentUser?.email)}
                                        </div>
                                        <div className="profile-info">
                                            <span className="profile-name">{userData?.displayName || 'Learner'}</span>
                                            <span className="profile-level">{levelInfo.icon} Level {levelInfo.level}</span>
                                        </div>
                                    </div>
                                    <div className="menu-divider" />
                                    <NavLink to="/profile" className="menu-item" onClick={() => setShowProfileMenu(false)}>
                                        <span className="menu-icon">👤</span>
                                        <span>Profile</span>
                                    </NavLink>
                                    <NavLink to="/analytics" className="menu-item" onClick={() => setShowProfileMenu(false)}>
                                        <span className="menu-icon">📈</span>
                                        <span>Analytics</span>
                                    </NavLink>
                                    <div className="menu-divider" />
                                    <button className="menu-item logout" onClick={handleLogout}>
                                        <span className="menu-icon">🚪</span>
                                        <span>Sign Out</span>
                                    </button>
                                </div>
                            )}
                        </header>
                    );
                })()}

                <div className="content-area">
                    {children}
                </div>
            </main>

            {/* Mobile Bottom Navigation */}
            {isMobile && (
                <nav className="bottom-nav">
                    {navItems.map((item) => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            className={({ isActive }) => `bottom-nav-item ${isActive ? 'active' : ''}`}
                        >
                            {({ isActive }) => (
                                <>
                                    <span className="nav-icon">{isActive ? item.activeIcon : item.icon}</span>
                                    <span className="nav-label">{item.label}</span>
                                    {isActive && <div className="nav-dot" />}
                                </>
                            )}
                        </NavLink>
                    ))}
                </nav>
            )}
        </div>
    );
}

export default AppShell;
